import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { authorizedFetch } from '@nimbitlabs/ui';
import { useAccountStore } from './accountStore';

export interface ReferralInfo {
  referralCode: string;
  referralUrl: string;
  referredByCode?: string;
}

export interface ReferralStats {
  totalReferrals: number;
  activeReferrals: number;
  referredAccounts: Array<{
    accountId: string;
    companyName: string;
    email: string;
    createdAt: string;
    isActive: boolean;
  }>;
}

interface ReferralState {
  info: ReferralInfo | null;
  stats: ReferralStats | null;
  loading: boolean;
  error: string | null;
  
  fetchReferralInfo: () => Promise<void>;
  fetchReferralStats: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useReferralStore = create<ReferralState>()(
  subscribeWithSelector((set) => ({
  info: null,
  stats: null,
  loading: false,
  error: null,
  
  fetchReferralInfo: async () => {
    set({ loading: true, error: null });
    try {
      const response = await authorizedFetch('/referral/info');
      if (!response.ok) throw new Error('Failed to fetch referral info');
      const data = await response.json();
      set({ 
        info: {
          referralCode: data.referralCode,
          referralUrl: data.referralUrl,
          referredByCode: data.referredByCode || undefined,
        },
        loading: false 
      });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch referral info', loading: false });
    }
  },
  
  fetchReferralStats: async () => {
    set({ loading: true, error: null });
    try {
      const response = await authorizedFetch('/referral/stats');
      if (!response.ok) throw new Error('Failed to fetch referral stats');
      const data = await response.json();
      set({ 
        stats: {
          totalReferrals: data.totalReferrals,
          activeReferrals: data.activeReferrals,
          referredAccounts: data.referredAccounts,
        },
        loading: false 
      });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch referral stats', loading: false });
    }
  },
  
  clearError: () => set({ error: null }),
  
  reset: () => set({ info: null, stats: null, loading: false, error: null }),
})));

// Hook to use referral stats
export function useReferralStats() {
  const stats = useReferralStore((state) => state.stats);
  const loading = useReferralStore((state) => state.loading);
  const fetchReferralStats = useReferralStore((state) => state.fetchReferralStats);
  
  return {
    stats,
    loading,
    fetchReferralStats,
  };
}

// Subscribe to account changes and auto-fetch data
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      useReferralStore.getState().fetchReferralInfo();
      useReferralStore.getState().fetchReferralStats();
    } else {
      useReferralStore.getState().reset();
    }
  }
);
